import React, {useState, useEffect, useCallback, useContext} from "react";
import {applyTheme, persistPreferences, restorePreferences, THEMES} from "../../core/theme/theme.config";
import type {AppTheme, ColorMode} from "../../core/theme/theme.config";
import "../../styles/component/header/header.scss";
import HeaderView from "./header.view";
import UserLoginContext from "../../context/userLoginContext";

interface HeaderProps {
    onNavigate: (path: string) => void;
    onLogout: () => void;
    onMenuToggle: () => void;
}

function Header(props: HeaderProps) {
    const {user} = useContext(UserLoginContext);
    const [activeTheme, setActiveTheme] = useState<AppTheme>(() => {
        const {theme, mode} = restorePreferences();
        applyTheme(theme, mode);
        return theme;
    });

    const [mode, setMode] = useState<ColorMode>(() => {
        return restorePreferences().mode;
    });

    // Cross-tab synchronization - listen to storage changes
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === "pn:theme" && e.newValue) {
                const newTheme = THEMES.find(t => t.id === e.newValue);
                if (newTheme) {
                    setActiveTheme(newTheme);
                    applyTheme(newTheme, mode);
                }
            } else if (e.key === "pn:mode" && e.newValue) {
                const newMode = e.newValue as ColorMode;
                if (newMode === "dark" || newMode === "light") {
                    setMode(newMode);
                    applyTheme(activeTheme, newMode);
                }
            }
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, [activeTheme, mode]);

    const handleThemeChange = useCallback((theme: AppTheme) => {

        document.documentElement.classList.add("theme-switching");

        applyTheme(theme, mode);
        persistPreferences(theme.id, mode);
        setActiveTheme(theme);

        requestAnimationFrame(() => {
            document.documentElement.classList.remove("theme-switching");
        });
    }, [mode]);

    // const res=[];
    // const aactive=res.filter((i)=>i.sttaus=='acticvde')
    // const format=[...acitve,...ianctive]

    const handleModeToggle = useCallback(() => {
        const next: ColorMode = mode === "dark" ? "light" : "dark";
        document.documentElement.classList.add("theme-switching");

        applyTheme(activeTheme, next);
        persistPreferences(activeTheme.id, next);
        setMode(next);

        requestAnimationFrame(() => {
            document.documentElement.classList.remove("theme-switching");
        });
    }, [mode, activeTheme]);

    const isLight = mode === "light";

    return (
        <HeaderView
            role={user.roles[0]}
            mode={mode}
            activeTheme={activeTheme}
            onNavigate={props.onNavigate}
            onLogout={props.onLogout}
            onMenuToggle={props.onMenuToggle}
            handleThemeChange={handleThemeChange}
            handleModeToggle={handleModeToggle}
        />
    )
}

export default Header;
